import { CopyButton } from "@expert/common-ui";
import { CopyIcon } from "@expert/common-ui/assets";
import { Button, CopyButton as Copy, Group, Image, Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { removeCountryCode } from "../../../../shared-utils";
import {
    formatPhoneNumber,
    DEFAULT_COUNTRY_CODE,
    parsePhoneNumber,
    validatePhoneNumber,
} from "../../../../shared-utils";
import { useAnalytics } from "../../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import {
    useCallIntroStore,
    useIntegratedFlowStore,
    useProductOfferStore,
    useVerificationFlowStore,
    useVerifiedCustomerStore,
} from "../../../../stores";
import { SaleProgress } from "../../../../types/types-sales";
import { clearEligibilityQueryCache } from "../../hooks/useEligibility";
import classes from "./CustomerProfile.module.css";
import { EditIcon } from "./EditIcon";

function FormattedPhoneNumber({ mdn, shouldSkipValidation }: { mdn: string; shouldSkipValidation: boolean }) {
    if (shouldSkipValidation) {
        return <span data-testid="phone-number">{parsePhoneNumber(mdn).number}</span>;
    }
    const { valid } = validatePhoneNumber(mdn);
    if (!valid) {
        return <span data-testid="unexpected-phone-number">Participant</span>;
    }
    return <span data-testid="phone-number">{formatPhoneNumber(mdn, DEFAULT_COUNTRY_CODE).number}</span>;
}

export function CustomerProfile() {
    const { sessionId, customerAvatarUrl, isSimulatedVoiceTask, onEditCustomerInfo } = useSalesContext();
    const { dispatcher } = useAnalytics();
    const queryClient = useQueryClient();

    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const stepHistory = useVerificationFlowStore((state) => state.getStepHistory(sessionId));
    const callIntroStep = useCallIntroStore((state) => state.currentStep);
    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const hasDispositionedAnyProduct = products.some((product) => product.saleProgress !== SaleProgress.InProgress);

    const isVerificationComplete =
        stepHistory[stepHistory.length - 1] === "VerificationComplete" || callIntroStep === "IntroFlowCompleted";
    const shouldShowEditButton =
        isVerificationComplete && !hasDispositionedAnyProduct && callIntroStep !== "IntroFlowCompleted";

    const handleEditClick = () => {
        void dispatcher.dispatchBusinessEvent("EditCustomerInfoClicked");
        clearEligibilityQueryCache(queryClient, sessionId);
        useVerificationFlowStore.getState().pushStep(sessionId, "CustomerPhoneInput");
        useCallIntroStore.getState().pushStep("CustomerInfoCollection");
        useIntegratedFlowStore.getState().resetIntegratedStepStore(sessionId);
        useProductOfferStore.getState().resetSaleSession(sessionId);
        // Re-opening the verification form swaps the Customer tab's content but not the active tab;
        // ask the host to bring the Customer tab forward so the expert lands on the edit form.
        onEditCustomerInfo?.();
    };

    return (
        <Group justify={isVerificationComplete ? "space-between" : "center"} gap="0" align="top" wrap="nowrap">
            <Group gap="0" wrap="nowrap">
                {customerAvatarUrl !== undefined && (
                    <Image className={classes.profilePicture} data-testid="customer-avatar" src={customerAvatarUrl} />
                )}
                {isVerificationComplete && (
                    <Stack gap="0">
                        <Text
                            data-testid="customer-name"
                            size="sm"
                            truncate
                            className={`${classes.name} fs-mask`}
                            span
                            tt="capitalize"
                        >
                            {customer?.fullName?.toLowerCase()}
                        </Text>
                        {customer?.mdn ? (
                            <Group gap={4} wrap="nowrap">
                                <Text w="max-content" className={`${classes.customerIdentifier} fs-mask`} size="sm">
                                    <FormattedPhoneNumber
                                        mdn={customer.mdn}
                                        shouldSkipValidation={isSimulatedVoiceTask}
                                    />
                                </Text>
                                <CopyButton value={removeCountryCode(customer.mdn)} />
                            </Group>
                        ) : (
                            customer?.email && (
                                <Group gap="0">
                                    <Text
                                        w="max-content"
                                        className={`${classes.customerIdentifier} fs-mask`}
                                        size="xs"
                                        data-testid="customer-email"
                                    >
                                        {customer.email}
                                    </Text>
                                </Group>
                            )
                        )}
                        {customer?.metadata?.caseNumber && (
                            <Copy value={customer.metadata.caseNumber}>
                                {({ copied, copy }) => (
                                    <Group onClick={copy} gap={5} style={{ cursor: "pointer" }}>
                                        <Text
                                            w="max-content"
                                            c="gray.6"
                                            className="fs-mask"
                                            size="sm"
                                            data-testid="customer-case-number"
                                        >
                                            {copied ? "Copied" : `Case #${customer.metadata?.caseNumber}`}
                                        </Text>
                                        <Group gap={0}>
                                            <CopyIcon width="12" stroke="var(--mantine-color-gray-6)" />
                                        </Group>
                                    </Group>
                                )}
                            </Copy>
                        )}
                    </Stack>
                )}
            </Group>

            {shouldShowEditButton && (
                <Button
                    id="mdn-edit-button"
                    onClick={handleEditClick}
                    color="dark.6"
                    size="xs"
                    variant="default"
                    data-testid="customer-profile-edit-button"
                    px="xs"
                >
                    <EditIcon />
                </Button>
            )}
        </Group>
    );
}
